<template>
    <div
        class="d-flex flex-column"
        v-bind:class="{ 'h-100': isSidebar }"
        data-test-role="navProgramsItems">
        <div
            v-if="isSidebar"
            class="text-end">
            <header-icon
                data-test-role="navProgramsDismiss"
                icon-name="x-lg"
                v-bind:aria-label="closeProgramSelectorLabel"
                class="bg-white p-3 m-1"
                v-on:click="dismissDialog" />
        </div>
        <div
            class="d-flex justify-content-center h5 m-0 fw-bold px-3 py-2 text-center"
            data-test-role="agencyProgramName">
            {{ $app.ProgramInfo.AgencyDisplayName }} - {{ $app.ProgramInfo.DisplayName }}
        </div>
        <div
            class="d-flex flex-column flex-md-row text-nowrap position-relative flex-grow-1"
            style="min-height: 0">
            <div
                class="px-4 py-3 overflow-auto flex-grow-1"
                v-bind:style="favAndRecentsPanelStyle">
                <div
                    v-if="favProgramItems.length > 0"
                    class="mb-3">
                    <strong>{{ favoritesHeaderText }}</strong>
                    <nav-program-list
                        v-bind:program-items="favProgramItems"
                        v-on:fav-clicked="toggleFav" />
                </div>
                <div>
                    <strong>{{ recentsHeaderText }}</strong>
                    <nav-program-list
                        v-bind:program-items="recentProgramItems"
                        v-on:fav-clicked="toggleFav" />
                </div>
                <div class="mt-4 text-primary fw-bold d-flex align-items-center">
                    <pc-button-icon
                        outline
                        icon="plus-circle"
                        class="btn-lg border-0 btn-outline-primary d-flex align-items-center shadow-none"
                        v-on:click="$app.setHref($app.AddProgramsUrl)">
                        <div class="ms-2">
                            {{ registerProgramsButtonLabel }}
                        </div>
                    </pc-button-icon>
                </div>
            </div>
            <div
                class="d-flex flex-column flex-grow-1 px-0 pt-3"
                v-bind:style="allProgramsPanelStyle">
                <strong class="px-4">{{ allProgramsHeaderText }}</strong>
                <div
                    v-if="showProgramFilter"
                    class="px-4">
                    <pc-search
                        ref="programFilterSearch"
                        v-on:search="onProgramFilterSearch" />
                </div>
                <div class="overflow-auto d-flex flex-column px-4 pb-3">
                    <div
                        v-for="agency in programItemsByAgency"
                        v-bind:key="agency.name"
                        class="mt-4">
                        <div class="pc-nav-program-logo-wrapper">
                            <program-logo
                                v-bind:agency-img-id="agency.imgId"
                                v-bind:aria-label="getAgencyLogoImageLabel(agency)" />
                        </div>
                        <div class="text-truncate">
                            {{ agency.name }}
                        </div>
                        <nav-program-list
                            is-all-programs-list
                            v-bind:program-items="agency.programItems"
                            v-bind:agency-name="agency.name"
                            v-on:fav-clicked="toggleFav" />
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
    import programLogo from "./programLogo.vue";
    import navProgramList from "./navProgramList.vue";
    import headerIcon from "./header-icon.vue";
    import { getDomainName, escapeRegex } from "Util/util.js";
    import { mapI18n } from "Util/i18n";
    import { KEYS, NAMESPACE } from "./i18n/navPrograms.i18n";
    import { responsiveManager } from "Responsive/manager";
    import { useAvailableProgramsStore } from "./availableProgramsStore";
    import { mapStores } from "pinia";

    export default {
        components: {
            programLogo,
            navProgramList,
            headerIcon
        },
        props: {
            isSidebar: { type: Boolean, required: false, default: false }
        },
        data() {
            return {
                favoritesMap: {},
                recentsList: [],
                programFilterSearchTerm: null
            };
        },
        computed: {
            showProgramFilter() {
                return this.programs.length >= 10;
            },
            favAndRecentsPanelStyle() {
                // Make sure the favorites bar grows with the height of the screen. The "All Programs"
                // section tends to take up the entire program selector if a min-height isn't set
                // since it is so much larger than the recents/favorites bar
                let height = 57; // button height
                height += 39 + 39 * this.recentProgramItems.length;
                if (this.favProgramItems.length > 0) {
                    height += 39 + 39 * this.favProgramItems.length;
                }

                return {
                    "min-height": `min(${height}px, 30vh)`,
                    "flex-basis": responsiveManager.md.value ? "50%" : "30%"
                };
            },
            allProgramsPanelStyle() {
                return {
                    "min-height": "calc(min(450px, 45vh) - 2.5rem)",
                    "flex-basis": responsiveManager.md.value ? "50%" : "70%"
                };
            },
            programFavIconClasses() {
                return [
                    "btn",
                    "border-0",
                    "p-2",
                    "rounded-circle",
                    "btn-outline-primary",
                    "d-flex",
                    "align-items-center"
                ];
            },
            programs() {
                return this.availableProgramsStore.availablePrograms;
            },
            programItemsByAgency() {
                const map = {};
                let filteredPrograms = this.programs;
                if (this.showProgramFilter && this.programFilterSearchTerm) {
                    const regex = new RegExp(escapeRegex(this.programFilterSearchTerm), "i");
                    filteredPrograms = this.programs.filter(
                        (p) => regex.test(p.AgencyDisplayName) || regex.test(p.DisplayName)
                    );
                }
                filteredPrograms.forEach((program) => {
                    if (!map[program.AgencyDisplayName]) {
                        map[program.AgencyDisplayName] = {
                            name: program.AgencyDisplayName,
                            imgId: program.AgencyImgId,
                            programItems: []
                        };
                    }
                    const programMenuItem = {
                        url: this.getContextSensitiveProgramUrl(program),
                        display: program.DisplayName,
                        isFavorite: this.favoritesMap.hasOwnProperty(program.ProgramId),
                        programId: program.ProgramId
                    };
                    map[program.AgencyDisplayName].programItems.push(programMenuItem);
                });

                return Object.entries(map).map((kvp) => kvp[1]);
            },
            favProgramItems() {
                return this.programs
                    .filter((program) => this.favoritesMap.hasOwnProperty(program.ProgramId))
                    .map((program) => ({
                        url: this.getContextSensitiveProgramUrl(program),
                        display: `${program.AgencyDisplayName} - ${program.DisplayName}`,
                        isFavorite: true,
                        programId: program.ProgramId
                    }));
            },
            recentProgramItems() {
                return this.recentsList
                    .map((programId) => this.programs.find((p) => p.ProgramId === programId))
                    .filter((p) => p != undefined)
                    .map((program) => ({
                        url: this.getContextSensitiveProgramUrl(program),
                        display: `${program.AgencyDisplayName} - ${program.DisplayName}`,
                        isFavorite: this.favoritesMap.hasOwnProperty(program.ProgramId),
                        programId: program.ProgramId
                    }));
            },
            favoritesCOLSKey() {
                const userId = this.$app.LoggedInUser.PublicId;
                return `cols_pc_${userId}_favorites`;
            },
            recentsCOLSKey() {
                const userId = this.$app.LoggedInUser.PublicId;
                return `cols_pc_${userId}_recent`;
            },
            ...mapI18n(KEYS, NAMESPACE),
            ...mapStores(useAvailableProgramsStore)
        },
        created() {
            this.availableProgramsStore.updateAvailablePrograms(this.$app.AvailableProgramsInfo);
            this.fillFavoritesMap();
            this.fillRecentsList();
        },
        mounted() {
            // applies to mobile view, desktop uses v-show and this gets called when page is mounted
            // awaiting next tick was not doing the trick, so use a timeout instead.
            setTimeout(() => {
                const selectedProgram = this.$el.querySelector(".pc-selected-program");
                selectedProgram?.scrollIntoView({ behavior: "auto", block: "nearest" });
            }, 10);
        },
        methods: {
            onProgramFilterSearch(searchTerm) {
                this.programFilterSearchTerm = searchTerm;
            },
            dismissDialog() {
                this.$app.closeDialog();
            },
            toggleFav(programItem) {
                if (this.favoritesMap.hasOwnProperty(programItem.programId)) {
                    delete this.favoritesMap[programItem.programId];
                } else {
                    this.favoritesMap[programItem.programId] = programItem.programId;
                }
                this.$app.setCOLS_item(this.favoritesCOLSKey, this.favoritesMap);
            },
            async fillFavoritesMap() {
                this.favoritesMap = {};
                const savedMap = (await this.$app.getCOLS_item(this.favoritesCOLSKey)) ?? {};
                this.programs.forEach((program) => {
                    if (savedMap.hasOwnProperty(program.ProgramId)) {
                        this.favoritesMap[program.ProgramId] = program.ProgramId;
                    }
                });
            },
            async fillRecentsList() {
                this.recentsList = [this.$app.ProgramInfo.ProgramId];
                const programIdSet = new Set(this.recentsList);

                const savedList = await this.$app.getCOLS_item(this.recentsCOLSKey);
                if (Array.isArray(savedList)) {
                    savedList.forEach((programId) => {
                        if (!programIdSet.has(programId)) {
                            programIdSet.add(programId);
                            this.recentsList.push(programId);
                        }
                    });
                }
                //include only the last 5 programs
                this.recentsList = this.recentsList.slice(0, 5);
                this.$app.setCOLS_item(this.recentsCOLSKey, this.recentsList);
            },
            getContextSensitiveProgramUrl(program) {
                let hostName = program.HostName ? `${program.HostName}.` : "";
                const domainName = getDomainName(window.location.href);
                if (domainName === "localhost") {
                    hostName = "";
                }
                const url = this.$app.ProgramChangeUrl;
                const programId = program.ProgramId;
                return `https://${hostName}${domainName}${url}?ProgramId=${programId}`;
            },
            getAgencyLogoImageLabel(agency) {
                return this.$app.res({
                    namespace: NAMESPACE,
                    key: KEYS.agencyLogoImageLabel,
                    values: {
                        agency: agency.name
                    }
                });
            }
        }
    };
</script>

<style lang="scss" scoped>
    @import "./_nav-style-vars.scss";

    .prog-not-fav {
        svg {
            color: transparent;
        }
        &:hover {
            svg {
                color: inherit;
            }
        }
    }
</style>
